import { DeviationTypes } from '../../types/deviation';
import { toMediaSrcFile } from './helpers';
var defaultFullview = {
    type: 'unknown',
    src: '',
    width: 1000,
    height: 1000,
    isDefault: true,
};
var MediaHandler = /** @class */ (function () {
    function MediaHandler() {
    }
    MediaHandler.prototype.isGif = function (deviation) {
        if (deviation && deviation.isGif) {
            return true;
        }
        if (!deviation.media || !deviation.media.types) {
            return false;
        }
        return deviation.media.types.find(function (file) { return file.t === 'gif'; }) ? true : false;
    };
    MediaHandler.prototype.getDeviationType = function (deviation) {
        if (!deviation) {
            return DeviationTypes.UNKNOWN;
        }
        // New type handling backend now defines
        if (deviation.type) {
            // TODO: we removed motionbook on the frontend, clean this up once the back-end stops sending it
            if (deviation.type !== 'motionbook') {
                return deviation.type;
            }
        }
        // Legacy duck typing below
        // polls
        if (deviation.poll) {
            return DeviationTypes.POLL;
        }
        // commission
        if (deviation.commission) {
            return DeviationTypes.COMMISSION;
        }
        // status
        if (deviation.status) {
            return DeviationTypes.STATUS;
        }
        // Freeform Text (Journal - Status - Literature)
        if (deviation.textContent) {
            if (deviation.isJournal) {
                return DeviationTypes.JOURNAL;
            }
            return DeviationTypes.LITERATURE;
        }
        // From here we should have some sort of media
        if (!deviation.media ||
            !deviation.media.types ||
            deviation.media.types.length === 0) {
            return DeviationTypes.UNKNOWN;
        }
        var fileTypes = {};
        for (var _i = 0, _a = deviation.media.types; _i < _a.length; _i++) {
            var file = _a[_i];
            fileTypes[file.t] = true;
        }
        // Video
        if (deviation.isVideo || fileTypes.video) {
            return DeviationTypes.FILM;
        }
        // PDF
        if (fileTypes.pdf) {
            return DeviationTypes.PDF;
        }
        // GIF
        if (deviation.isGif || fileTypes.gif) {
            return DeviationTypes.IMAGE;
        }
        return DeviationTypes.IMAGE;
    };
    MediaHandler.prototype.getLargestFullview = function (deviation) {
        var type = this.getDeviationType(deviation);
        if (type === DeviationTypes.JOURNAL || type === DeviationTypes.LITERATURE) {
            return defaultFullview;
        }
        return this._getLargestFullview(deviation);
    };
    MediaHandler.prototype.getThumbDimensions = function (deviation) {
        var fv = this._getLargestFullview(deviation);
        var type = this.getDeviationType(deviation);
        if (type === DeviationTypes.TIER) {
            return { width: 1840, height: 1000 };
        }
        if (!fv ||
            !fv.width ||
            !fv.height ||
            [
                DeviationTypes.JOURNAL,
                DeviationTypes.LITERATURE,
                DeviationTypes.STATUS,
                DeviationTypes.COMMISSION,
            ].includes(type)) {
            return { width: 1000, height: 1000 };
        }
        return { width: fv.width, height: fv.height };
    };
    MediaHandler.prototype.getSmallestThumb = function (deviation, options) {
        var thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        var small = this._getThumbsSortedSmallToBig(thumbs);
        return small[0];
    };
    MediaHandler.prototype.getSmallestThumbThatFullyCovers = function (deviation, width, height, fallback, options) {
        var thumbs = this.getThumbSet(deviation, options);
        if (!thumbs) {
            return;
        }
        // Allow slightly smaller than the requested block, the scaling
        // rules allow for 25%~ smaller to be scaled up so make this match
        // those. We force portraits to match the width.
        var allowedWidth = width; // * 0.85;
        var allowedHeight = height; // * 0.85;
        if (width < height) {
            allowedWidth = width;
        }
        var smallest = this._getThumbsSortedSmallToBig(thumbs).find(function (img) {
            var _a = img.width, imgWidth = _a === void 0 ? 0 : _a, _b = img.height, imgHeight = _b === void 0 ? 0 : _b;
            return imgWidth >= allowedWidth && imgHeight >= allowedHeight;
        });
        // Allow caller to determine the fallback
        if (!smallest && fallback) {
            return fallback(thumbs);
        }
        return smallest;
    };
    MediaHandler.prototype._getThumbsSortedSmallToBig = function (thumbs) {
        if (!thumbs) {
            return [];
        }
        return thumbs.sort(function (a, b) {
            var asc = (a.width || 0) * (a.height || 0);
            var bsc = (b.width || 0) * (b.height || 0);
            if (asc === bsc) {
                return 0;
            }
            return asc < bsc ? -1 : 1;
        });
    };
    MediaHandler.prototype.getFullviewForSize = function (deviation, width, height) {
        var _this = this;
        var fullviews = this._getFullviews(deviation);
        if (!fullviews.length) {
            return;
        }
        // sort by resolution small to big
        fullviews.sort(function (a, b) {
            return _this._getFilePixelSize(a) - _this._getFilePixelSize(b);
        });
        // find smallest fitting file
        if (width && height) {
            var file = fullviews.find(function (f) {
                if (!f.w || !f.h) {
                    return false;
                }
                return f.w >= width && f.h >= height;
            });
            if (file) {
                return toMediaSrcFile(deviation, file);
            }
        }
        // if no fitting file was found return largest one
        return toMediaSrcFile(deviation, fullviews[fullviews.length - 1]);
    };
    MediaHandler.prototype._getFullviews = function (deviation) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        var files = deviation && deviation.media && deviation.media.types;
        // for gif images only the gif file is a valid fullview
        if (this.isGif(deviation)) {
            return files.filter(function (file) { return file && /^fullview/.test(file.t); });
        }
        return files.filter(function (file) { return file && /^fullview|preview|original/.test(file.t); });
    };
    MediaHandler.prototype._getFilePixelSize = function (file) {
        return (file.w || 0) * (file.h || 0);
    };
    MediaHandler.prototype._mightBeCompressedFilesList = function (deviation) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return false;
        }
        // Assume single file whatever it is to be safe
        if (deviation.media.types.length === 1) {
            return true;
        }
        var types = deviation.media.types.map(function (f) { return f.t; });
        // social_preview bug T38231
        if (types.length === 2 &&
            types.includes('fullview') &&
            types.includes('social_preview')) {
            return true;
        }
        return false;
    };
    MediaHandler.prototype.getDeviationFile = function (deviation, fileType) {
        var types = deviation && deviation.media && deviation.media.types;
        var item = types && types.find(function (f) { return f.t === fileType; });
        if (item) {
            return toMediaSrcFile(deviation, item);
        }
        return undefined;
    };
    MediaHandler.prototype.getThumbSet = function (deviation, options) {
        var thumbFileTypes = [
            '150',
            '200H',
            '300W',
            '200T',
            '250T',
            '350T',
            '400T',
            '375W',
            '414W',
            '750W',
            '828W',
        ];
        // Check if this deviation may be compressed by the editor, in this
        // case we can have reduced files array and need to allow
        if (this._mightBeCompressedFilesList(deviation)) {
            thumbFileTypes.push('preview', 'fullview', 'gif');
        }
        if (options && options.allowPreview) {
            thumbFileTypes.push('preview');
        }
        if (options && options.allowFullview) {
            thumbFileTypes.push('fullview');
            thumbFileTypes.push('fullview_unpublished');
        }
        if (options && options.allowSquareCrop) {
            thumbFileTypes.push('92S');
        }
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        return deviation.media.types
            .filter(function (file) { return thumbFileTypes.includes(file.t); })
            .map(function (file) { return toMediaSrcFile(deviation, file); });
    };
    MediaHandler.prototype.getMediaByType = function (deviation, fileType) {
        if (!deviation || !deviation.media || !deviation.media.types) {
            return [];
        }
        return deviation.media.types
            .filter(function (file) { return file.t === fileType; })
            .map(function (file) { return toMediaSrcFile(deviation, file); });
    };
    MediaHandler.prototype._getLargestFullview = function (deviation) {
        var fvs = this._getFullviews(deviation);
        if (fvs.length === 0) {
            return defaultFullview;
        }
        var fv = fvs.reduce(function (prev, curr) {
            if ((prev.w || 0) > (curr.w || 0) || (prev.h || 0) > (curr.h || 0)) {
                return prev;
            }
            return curr;
        });
        return ((fv && toMediaSrcFile(deviation, fv)) ||
            defaultFullview);
    };
    return MediaHandler;
}());
export default MediaHandler;
//# sourceMappingURL=MediaHandler.js.map